import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, can } from "@/context/AuthContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Users, UsersThree, Ticket, ArrowLeft,
  IdentificationCard, CreditCard, Wallet, ChartBar,
  Play, Check
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";

const fmtEUR = (n) => (n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtNum = (n) => (n || 0).toLocaleString("es-ES");

export default function MatchDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [club, setClub] = useState({ club_name: "Entradas Deportivas" });
  const [notFound, setNotFound] = useState(false);
  const isAdmin = can(user, "admin");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${id}/summary`);
      setData(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/club-config");
        if (data) setClub({ club_name: data.club_name || "Entradas Deportivas" });
      } catch { /* ignore */ }
    })();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [load]);

  if (notFound) {
    return (
      <div className="text-center py-24">
        <div className="font-display font-bold text-3xl uppercase text-slate-700">Partido no encontrado</div>
        <Link to="/partidos" className="inline-block mt-6 text-[#2563EB] hover:underline">← Volver a partidos</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="text-slate-500 text-center py-16">Cargando...</div>;
  }

  const match = data.match;
  const aforo = data.aforo || {};
  const eco = data.economy || {};
  const byTipo = aforo.by_tipo || {};
  const recent = data.recent_accesses || [];

  const socios = byTipo.socio || 0;
  const generales = (byTipo.general || 0) + (byTipo.infantil || 0);
  const ocupacion = aforo.ocupacion || 0;
  const aforoDentro = aforo.dentro || 0;
  const aforoMax = aforo.aforo_maximo || 0;

  const pieData = [
    { name: "Socios", value: socios, color: "#22C55E" },
    { name: "Generales", value: byTipo.general || 0, color: "#007AFF" },
    { name: "Infantiles", value: byTipo.infantil || 0, color: "#F97316" },
    { name: "Invitaciones", value: byTipo.invitacion || 0, color: "#A855F7" },
    { name: "Otros", value: (byTipo.prensa || 0) + (byTipo.directiva || 0) + (byTipo.patrocinador || 0), color: "#94A3B8" },
  ].filter((d) => d.value > 0);
  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);

  const hourly = data.hourly || [];

  const activate = async () => {
    try {
      await api.post(`/matches/${id}/activate`);
      toast.success("Partido activado. Ya puedes escanear accesos");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const finish = async () => {
    if (!window.confirm("¿Finalizar este partido?")) return;
    try {
      await api.post(`/matches/${id}/finish`);
      toast.success("Partido finalizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const estadoBadge = {
    activo: { label: "Activo", cls: "text-[#16a34a] bg-[#22C55E]/10 border-[#22C55E]/30" },
    finalizado: { label: "Finalizado", cls: "text-slate-600 bg-slate-100 border-slate-200" },
    pendiente: { label: "Pendiente", cls: "text-[#2563EB] bg-[#2563EB]/10 border-[#2563EB]/30" },
  }[match.estado] || { label: match.estado, cls: "bg-slate-100 border-slate-200" };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate("/partidos")} className="text-xs text-slate-500 hover:text-[#2563EB] flex items-center gap-1 mb-2" data-testid="back-to-partidos">
            <ArrowLeft size={14} /> Volver a partidos
          </button>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#2563EB] font-bold">Dashboard del partido</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl uppercase tracking-tighter" data-testid="match-dashboard-title">
            {club.club_name} <span className="text-slate-400">vs</span> {match.rival}
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>{match.competicion}</span>
            <span>•</span>
            <span>{match.fecha} {match.hora}</span>
            <span>•</span>
            <span>Temporada {match.temporada}</span>
            <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${estadoBadge.cls}`} style={{ borderRadius: 4 }}>
              {estadoBadge.label}
            </span>
          </div>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            {match.estado !== "activo" && match.estado !== "finalizado" && (
              <button data-testid="btn-activate-match" onClick={activate} className="h-10 px-4 bg-[#22C55E] hover:bg-[#16a34a] text-white text-xs font-display font-bold uppercase tracking-wider flex items-center gap-2 transition active:scale-[0.98]" style={{ borderRadius: 8 }}>
                <Play size={14} weight="bold" /> Activar
              </button>
            )}
            {match.estado === "activo" && (
              <button data-testid="btn-finish-match" onClick={finish} className="h-10 px-4 bg-slate-800 hover:bg-slate-900 text-white text-xs font-display font-bold uppercase tracking-wider flex items-center gap-2 transition active:scale-[0.98]" style={{ borderRadius: 8 }}>
                <Check size={14} weight="bold" /> Finalizar
              </button>
            )}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={UsersThree} color="#22C55E"
          label="Personas dentro" value={fmtNum(aforoDentro)}
          sub={`de ${fmtNum(aforoMax)}`} progress={ocupacion}
          testid="kpi-personas-dentro"
        />
        <KpiCard icon={Users} color="#2563EB" label="Socios entrados" value={fmtNum(socios)} sub="En este partido" testid="kpi-socios-entrados" />
        <KpiCard icon={Ticket} color="#F97316" label="Entradas generales" value={fmtNum(generales)} sub="En este partido" testid="kpi-generales" />
      </div>

      {/* Acciones rápidas */}
      <Card title="Acciones rápidas">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <QuickAction icon={Users} label="Gestionar socios" to="/socios" />
          <QuickAction icon={CreditCard} label="Registrar pago" to="/cuotas" />
          <QuickAction icon={Wallet} label="Caja del partido" to="/caja" />
          {can(user, "admin") && <QuickAction icon={IdentificationCard} label="Usuarios" to="/usuarios" />}
          <QuickAction icon={ChartBar} label="Aforo en vivo" to="/aforo" />
        </div>
      </Card>

      {/* Aforo + Últimos accesos + Entradas por tipo */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Aforo en directo" subtitle="Actualizado cada 4 s">
          <div className="flex flex-col items-center pt-2">
            <HalfGauge value={ocupacion} max={100} />
            <div className="-mt-20 text-center">
              <div className="font-display font-bold text-5xl tracking-tighter" data-testid="aforo-gauge-value">{fmtNum(aforoDentro)}</div>
              <div className="text-xs text-slate-500 mt-1">personas dentro</div>
            </div>
            <div className="flex items-center justify-between w-full mt-2 text-xs text-slate-500">
              <span>0</span><span>{fmtNum(aforoMax)}</span>
            </div>
            <div className="mt-3 text-sm font-medium" style={{ color: ocupacion < 60 ? "#16a34a" : ocupacion < 85 ? "#ea580c" : "#dc2626" }}>
              {ocupacion}% del aforo ocupado
            </div>
          </div>
        </Card>

        <Card title="Últimos accesos">
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-slate-100">
                  <th className="px-5 py-2 text-[9px] tracking-widest uppercase text-slate-500 font-bold">Hora</th>
                  <th className="px-2 py-2 text-[9px] tracking-widest uppercase text-slate-500 font-bold">Tipo</th>
                  <th className="px-2 py-2 text-[9px] tracking-widest uppercase text-slate-500 font-bold">Nombre</th>
                  <th className="px-5 py-2 text-[9px] tracking-widest uppercase text-slate-500 font-bold">Acción</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-500">Sin accesos aún</td></tr>
                )}
                {recent.slice(0, 6).map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-5 py-2 tabular-nums text-slate-600">
                      {new Date(a.timestamp).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </td>
                    <td className="px-2 py-2 text-slate-700 uppercase text-[10px] font-bold">{a.tipo || "—"}</td>
                    <td className="px-2 py-2 truncate max-w-[160px]">
                      {a.member_name || `Entrada ${a.tipo}`}
                      {a.numero_socio && <span className="text-slate-500 ml-1">#{a.numero_socio}</span>}
                    </td>
                    <td className="px-5 py-2">
                      <span
                        className={`px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold border ${
                          a.result === "verde" ? "text-[#16a34a] bg-[#22C55E]/10 border-[#22C55E]/30" :
                          a.result === "naranja" ? "text-[#ea580c] bg-[#F97316]/10 border-[#F97316]/30" :
                          "text-[#dc2626] bg-[#EF4444]/10 border-[#EF4444]/30"
                        }`}
                        style={{ borderRadius: 4 }}
                      >
                        {a.direction === "out" ? "Salida" : "Entrada"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Distribución de entradas">
          {pieTotal === 0 ? (
            <div className="text-center text-slate-500 text-sm py-12">Sin entradas registradas</div>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" innerRadius={42} outerRadius={70} paddingAngle={2} stroke="none">
                      {pieData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {pieData.map((p) => (
                  <div key={p.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
                      <span className="text-slate-700">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-600 tabular-nums">{p.value}</span>
                      <span className="text-slate-400 tabular-nums w-9 text-right">{pieTotal ? Math.round((p.value / pieTotal) * 100) : 0}%</span>
                    </div>
                  </div>
                ))}
                <div className="pt-2 mt-1 border-t border-slate-100 flex justify-between text-sm">
                  <span className="font-bold">Total</span>
                  <span className="font-display font-bold text-lg">{pieTotal}</span>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Afluencia + Caja */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Afluencia por horas">
          {hourly.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-12">Sin datos aún</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <AreaChart data={hourly} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="hourly" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="hora" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 6, color: "#0F172A", fontSize: 12 }} />
                  <Area type="monotone" dataKey="valor" stroke="#2563EB" strokeWidth={2} fill="url(#hourly)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Caja del partido" subtitle="Resumen económico">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="border border-[#22C55E]/30 bg-[#22C55E]/5 p-3" style={{ borderRadius: 8 }}>
              <div className="text-[9px] tracking-widest uppercase text-[#16a34a] font-bold">Ingresos</div>
              <div className="font-display font-bold text-2xl tracking-tighter text-[#16a34a] mt-1" data-testid="match-ingresos">{fmtEUR(eco.ingresos)}</div>
            </div>
            <div className="border border-[#EF4444]/30 bg-[#EF4444]/5 p-3" style={{ borderRadius: 8 }}>
              <div className="text-[9px] tracking-widest uppercase text-[#dc2626] font-bold">Gastos</div>
              <div className="font-display font-bold text-2xl tracking-tighter text-[#dc2626] mt-1" data-testid="match-gastos">{fmtEUR(eco.gastos)}</div>
            </div>
          </div>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {["taquilla", "cantina", "rifas", "merchandising"].map((cat) => (
              <div key={cat} className="flex justify-between text-xs">
                <span className="text-slate-600 capitalize">{cat}</span>
                <span className="font-medium tabular-nums">{fmtEUR(eco.ingresos_by_cat?.[cat] || 0)}</span>
              </div>
            ))}
            {["arbitros", "seguridad", "limpieza"].map((cat) => (
              <div key={cat} className="flex justify-between text-xs">
                <span className="text-slate-600 capitalize">{cat}</span>
                <span className="font-medium tabular-nums text-[#dc2626]/80">{fmtEUR(eco.gastos_by_cat?.[cat] || 0)}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Link to="/ingresos" className="h-9 text-xs flex items-center justify-center border border-[#22C55E]/40 text-[#16a34a] hover:bg-[#22C55E]/10 font-bold uppercase tracking-wider" style={{ borderRadius: 8 }}>Ver ingresos</Link>
            <Link to="/gastos" className="h-9 text-xs flex items-center justify-center border border-[#EF4444]/40 text-[#dc2626] hover:bg-[#EF4444]/10 font-bold uppercase tracking-wider" style={{ borderRadius: 8 }}>Ver gastos</Link>
          </div>
          <div
            className="mt-3 px-3 py-2 text-center font-display font-bold border"
            style={{
              borderRadius: 8,
              background: (eco.beneficio || 0) >= 0 ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
              borderColor: (eco.beneficio || 0) >= 0 ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)",
              color: (eco.beneficio || 0) >= 0 ? "#16a34a" : "#dc2626",
            }}
            data-testid="match-beneficio"
          >
            <span className="text-[10px] tracking-widest uppercase opacity-70 mr-2">Beneficio</span>
            {fmtEUR(eco.beneficio)}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }}>
      <div className="mb-4">
        <h3 className="font-display font-bold text-xl uppercase tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, value, sub, progress, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-4 flex flex-col shadow-card" style={{ borderRadius: 12 }} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 leading-tight max-w-[80%]">{label}</div>
        <div className="h-8 w-8 flex items-center justify-center" style={{ background: color + "20", borderRadius: 8 }}>
          <Icon size={16} weight="bold" style={{ color }} />
        </div>
      </div>
      <div className="font-display font-bold text-3xl tracking-tighter mt-2" style={{ color }}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
      {typeof progress === "number" && (
        <div className="mt-2">
          <div className="h-1 bg-slate-100" style={{ borderRadius: 999 }}>
            <div className="h-full transition-all duration-700" style={{ width: `${Math.min(100, progress)}%`, background: color, borderRadius: 999 }} />
          </div>
          <div className="text-[10px] mt-1 font-bold" style={{ color }}>{progress}%</div>
        </div>
      )}
    </div>
  );
}

function HalfGauge({ value, max }) {
  const pct = Math.min(100, (value / max) * 100);
  const angle = (pct / 100) * 180;
  const color = pct < 60 ? "#22C55E" : pct < 85 ? "#F97316" : "#EF4444";
  const radius = 70;
  const circ = Math.PI * radius;
  const dash = (angle / 180) * circ;
  return (
    <div className="relative w-48 h-28">
      <svg viewBox="0 0 160 90" className="absolute inset-0 w-full h-full">
        <path d="M 10 80 A 70 70 0 0 1 150 80" stroke="#E2E8F0" strokeWidth="14" fill="none" strokeLinecap="round" />
        <path
          d="M 10 80 A 70 70 0 0 1 150 80"
          stroke={color} strokeWidth="14" fill="none" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          style={{ transition: "stroke-dasharray 800ms ease-out, stroke 300ms" }}
        />
      </svg>
    </div>
  );
}

function QuickAction({ icon: Icon, label, to }) {
  return (
    <Link
      to={to}
      className="border border-slate-200 hover:border-[#2563EB] hover:bg-[#2563EB]/5 p-4 flex flex-col items-center gap-2 transition text-center shadow-card"
      style={{ borderRadius: 12 }}
    >
      <div className="h-10 w-10 flex items-center justify-center bg-[#2563EB]/10" style={{ borderRadius: 8 }}>
        <Icon size={20} className="text-[#2563EB]" weight="bold" />
      </div>
      <span className="text-xs font-bold leading-tight">{label}</span>
    </Link>
  );
}
